import { useCallback } from 'react';
import * as _ from 'lodash-es';

import { useAxiosClient } from '../store/AxiosClient';
import { useEntityMeta } from '../store/EntityMeta';
import { useShowLoading } from '../../contexts/ShowLoading';
import { useInfoDialog } from '../../contexts/InfoDialog';

import { useStateWithGetter } from '../../utils/useStateWithGetter';
import { useThrowError } from '../../utils/useThrowError';
import { translate } from '../../utils/translations';

import { BffCustomReportsApi, OutputTypeCode, useApi } from './generated';
import { HTTP_RESPONSE_ERROR_CODE } from '../../constants';

const downloadFile = (url: string, filename: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const useTestPrint = () => {
  const axiosClient = useAxiosClient();
  const customReportApi = useApi(axiosClient, BffCustomReportsApi);
  const { templateId, setTemplateRowVersion } = useEntityMeta();
  const [isApiRequesting, setIsApiRequesting, getIsApiRequesting] =
    useStateWithGetter(false);
  const { setShowLoading } = useShowLoading();
  const { throwError } = useThrowError();
  const infoDialog = useInfoDialog();

  const testPrint = useCallback(async () => {
    if (getIsApiRequesting()) return;

    setIsApiRequesting(true);
    setShowLoading(true);

    try {
      const {
        data: { success, data },
      } = await customReportApi.customReportsPostPreview(
        templateId,
        OutputTypeCode.Excel
      );

      if (success && data) {
        setTemplateRowVersion(data.templateRowVersion);
        downloadFile(data.outputResultFileUrl, data.outputResultFileUrl);
      } else {
        // リクエストに成功したが何かしらの理由でテスト出力に失敗した
        throwError(translate('COMMON.ERROR'));
      }
    } catch (e) {
      // リクエスト自体が失敗したパターン（200以外）
      // TODO: エラーハンドリングは後ほど実装
      const errorCode = _.get(e, 'response.data.errorCode');
      if (errorCode === HTTP_RESPONSE_ERROR_CODE.MSG_ERROR_APPCOMMON_009) {
        const ok = await infoDialog({
          message: translate('VALIDATE.MSG_ERROR_APPCOMMON_009'),
        });
        if (ok) return;
      }

      throwError(e);
    } finally {
      setIsApiRequesting(false);
      setShowLoading(false);
    }
  }, [customReportApi, templateId]);

  return { testPrint };
};
